/**
 * On-call mobile/tablet triage surface (Epic D1 / Gap #15).
 * Touch-oriented candidate review with MFA session gates and ack audit.
 */

import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  ListGroup,
  Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  MOBILE_DEVICE_BROWSER_MATRIX,
  MOBILE_TRIAGE_PHASE_CONSTRAINTS,
  createMobileTriageProfile,
  isMobileTriageSessionActive,
  recordMobileTriageDisposition,
  type MobileTriageAuditEntry,
} from "../utils/mobileTriageProfile";
import { usePortalWorkbenchSnapshot } from "../services/portalWorkbenchApi";

export default function MobileTriagePanel() {
  const [mfaSatisfied, setMfaSatisfied] = useState(true);
  const { snapshot, loading, error, reload } = usePortalWorkbenchSnapshot();
  const candidates = snapshot?.mobileTriage.candidates ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [audit, setAudit] = useState<MobileTriageAuditEntry[]>([]);

  const profile = useMemo(
    () =>
      createMobileTriageProfile({
        enabled: true,
        mfaSatisfied,
        sessionMaxMinutes: 30,
      }),
    [mfaSatisfied],
  );
  const sessionActive = isMobileTriageSessionActive(profile);
  const selected =
    candidates.find((c) => c.studyId === selectedId) ?? candidates[0] ?? null;

  const dispose = (disposition: "ack" | "defer" | "escalate") => {
    if (!selected) return;
    const result = recordMobileTriageDisposition({
      profile,
      actorId: "oncall-md",
      studyId: selected.studyId,
      candidateId: selected.studyId,
      disposition,
    });
    setAudit((prev) => [result.audit, ...prev]);
  };

  return (
    <Container fluid className="py-3" data-testid="mobile-triage-panel">
      <Alert variant="warning" className="mb-3">
        <strong>Assistive on-call triage</strong> —{" "}
        {MOBILE_TRIAGE_PHASE_CONSTRAINTS.clinicalUseNotice}
      </Alert>
      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Row className="g-3">
        <Col lg={7}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Touch candidate queue</span>
              <span>
                <Button size="sm" variant="primary" onClick={reload} disabled={loading} className="me-2">Refresh</Button>
                <Badge bg={sessionActive ? "success" : "secondary"}>
                  {sessionActive ? "MFA session active" : "Session locked"}
                </Badge>
              </span>
            </Card.Header>
            <Card.Body>
              <Form.Check
                type="switch"
                id="mobile-mfa"
                className="mb-3"
                label="MFA satisfied for this session"
                checked={mfaSatisfied}
                onChange={(e) => setMfaSatisfied(e.target.checked)}
                data-testid="mobile-triage-mfa"
              />
              <ListGroup className="mb-3">
                {loading ? (
                  <ListGroup.Item>Loading triage candidates...</ListGroup.Item>
                ) : candidates.length === 0 ? (
                  <ListGroup.Item className="text-muted">No stat, urgent, or quality-limited studies need mobile triage.</ListGroup.Item>
                ) : (
                  candidates.map((c) => (
                    <ListGroup.Item
                      key={c.studyId}
                      action
                      active={c.studyId === selected?.studyId}
                      onClick={() => setSelectedId(c.studyId)}
                      style={{ minHeight: 56 }}
                    >
                      <div className="fw-semibold">{c.label}</div>
                      <small>
                        {c.patientLabel} · {c.priority}
                        {c.confidencePercent != null ? ` · conf ${c.confidencePercent}%` : ""}
                      </small>
                    </ListGroup.Item>
                  ))
                )}
              </ListGroup>
              <div className="d-flex flex-wrap gap-2 mb-2">
                <Button
                  size="lg"
                  variant="outline-primary"
                  disabled={!selected}
                  as={Link as any}
                  to={selected ? `/eeg/${selected.studyId}` : "#"}
                  data-testid="mobile-triage-jump"
                >
                  Open raw EEG
                </Button>
                <Button
                  size="lg"
                  variant="success"
                  disabled={!sessionActive || !selected}
                  onClick={() => dispose("ack")}
                >
                  Ack
                </Button>
                <Button
                  size="lg"
                  variant="warning"
                  disabled={!sessionActive || !selected}
                  onClick={() => dispose("defer")}
                >
                  Defer
                </Button>
                <Button
                  size="lg"
                  variant="danger"
                  disabled={!sessionActive || !selected}
                  onClick={() => dispose("escalate")}
                >
                  Escalate
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={5}>
          <Card className="mb-3">
            <Card.Header>Notify → triage → ack audit</Card.Header>
            <ListGroup variant="flush" data-testid="mobile-triage-audit">
              {audit.length === 0 ? (
                <ListGroup.Item className="text-muted">No events yet</ListGroup.Item>
              ) : (
                audit.slice(0, 12).map((entry, i) => (
                  <ListGroup.Item key={`${entry.atIso}-${i}`}>
                    <Badge bg="dark" className="me-2">
                      {entry.action}
                    </Badge>
                    {entry.candidateId || entry.studyId}
                    {entry.deepLink ? (
                      <div>
                        <small className="text-break">{entry.deepLink}</small>
                      </div>
                    ) : null}
                  </ListGroup.Item>
                ))
              )}
            </ListGroup>
          </Card>
          <Card>
            <Card.Header>Device / browser matrix</Card.Header>
            <Card.Body className="small" data-testid="mobile-device-matrix">
              {MOBILE_DEVICE_BROWSER_MATRIX.map((row) => (
                <div key={`${row.platform}-${row.browser}`}>
                  {row.platform} / {row.browser} {row.minVersion} —{" "}
                  <strong>{row.support}</strong>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
